import { z } from 'zod';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPES,
  DOCUMENT_VERIFICATION_STATUS,
  DocumentScope,
  ENTITY_ATTACHMENT_TYPES,
  LOAN_TYPES,
} from '../constants/document-types';
import { ListQuerySchema } from './common.dto';

/**
 * Attachment DTOs for Universal Document Attachment System
 *
 * Issue #497: Universal Document Attachment System
 *
 * Enables attaching documents to any entity type (Case, Deal, Consumer, etc.)
 * with document type classification and verification workflow.
 */

// ============================================================================
// ENTITY ATTACHMENT SCHEMAS
// ============================================================================

/**
 * Entity attachment type enum values for validation
 */
export const EntityAttachmentTypeSchema = z.enum([
  ENTITY_ATTACHMENT_TYPES.CASE,
  ENTITY_ATTACHMENT_TYPES.CONSUMER,
  ENTITY_ATTACHMENT_TYPES.LEAD,
  ENTITY_ATTACHMENT_TYPES.PARTNER,
  ENTITY_ATTACHMENT_TYPES.COMMENT,
  ENTITY_ATTACHMENT_TYPES.TASK,
  ENTITY_ATTACHMENT_TYPES.INTAKE_SUBMISSION,
  ENTITY_ATTACHMENT_TYPES.CUSTOM_ENTITY,
  ENTITY_ATTACHMENT_TYPES.BORROWING_ENTITY,
]);

// Note: Re-export as EntityAttachmentType for use in routers/components
export type EntityAttachmentType = z.infer<typeof EntityAttachmentTypeSchema>;

/**
 * Document type values for validation
 */
export const DocumentTypeSchema = z.enum([
  DOCUMENT_TYPES.TAX_RETURN_PERSONAL,
  DOCUMENT_TYPES.TAX_RETURN_BUSINESS,
  DOCUMENT_TYPES.BANK_STATEMENT_PERSONAL,
  DOCUMENT_TYPES.BANK_STATEMENT_BUSINESS,
  DOCUMENT_TYPES.PROFIT_LOSS_STATEMENT,
  DOCUMENT_TYPES.BALANCE_SHEET,
  DOCUMENT_TYPES.FINANCIAL_STATEMENT,
  DOCUMENT_TYPES.INCOME_VERIFICATION,
  DOCUMENT_TYPES.ASSET_STATEMENT,
  DOCUMENT_TYPES.PROPERTY_APPRAISAL,
  DOCUMENT_TYPES.TITLE_REPORT,
  DOCUMENT_TYPES.PROPERTY_INSURANCE,
  DOCUMENT_TYPES.PROPERTY_PHOTOS,
  DOCUMENT_TYPES.SCOPE_OF_WORK,
  DOCUMENT_TYPES.CONSTRUCTION_BUDGET,
  DOCUMENT_TYPES.PURCHASE_AGREEMENT,
  DOCUMENT_TYPES.DEED,
  DOCUMENT_TYPES.SURVEY,
  DOCUMENT_TYPES.ENVIRONMENTAL_REPORT,
  DOCUMENT_TYPES.INSPECTION_REPORT,
  DOCUMENT_TYPES.DRIVERS_LICENSE,
  DOCUMENT_TYPES.PASSPORT,
  DOCUMENT_TYPES.SSN_CARD,
  DOCUMENT_TYPES.UTILITY_BILL,
  DOCUMENT_TYPES.STATE_ID,
  DOCUMENT_TYPES.ARTICLES_OF_INCORPORATION,
  DOCUMENT_TYPES.OPERATING_AGREEMENT,
  DOCUMENT_TYPES.GOOD_STANDING_CERTIFICATE,
  DOCUMENT_TYPES.BYLAWS,
  DOCUMENT_TYPES.PARTNERSHIP_AGREEMENT,
  DOCUMENT_TYPES.TRUST_AGREEMENT,
  DOCUMENT_TYPES.EIN_LETTER,
  DOCUMENT_TYPES.SIGNED_LOAN_APPLICATION,
  DOCUMENT_TYPES.PROMISSORY_NOTE,
  DOCUMENT_TYPES.DEED_OF_TRUST,
  DOCUMENT_TYPES.CLOSING_STATEMENT,
  DOCUMENT_TYPES.LOAN_ESTIMATE,
  DOCUMENT_TYPES.CLOSING_DISCLOSURE,
  DOCUMENT_TYPES.MORTGAGE_NOTE,
  DOCUMENT_TYPES.GUARANTY_AGREEMENT,
  DOCUMENT_TYPES.AUTHORIZATION_FORM,
  DOCUMENT_TYPES.CREDIT_AUTHORIZATION,
  DOCUMENT_TYPES.BACKGROUND_CHECK_CONSENT,
  DOCUMENT_TYPES.THIRD_PARTY_AUTHORIZATION,
  DOCUMENT_TYPES.PROOF_OF_FUNDS,
  DOCUMENT_TYPES.CONTRACTOR_LICENSE,
  DOCUMENT_TYPES.EXPERIENCE_DOCUMENTATION,
  DOCUMENT_TYPES.LETTER_OF_EXPLANATION,
  DOCUMENT_TYPES.CONSTRUCTION_PLANS,
  DOCUMENT_TYPES.BUILDING_PERMITS,
  DOCUMENT_TYPES.RENT_ROLL,
  DOCUMENT_TYPES.LEASE_AGREEMENTS,
  DOCUMENT_TYPES.OTHER,
]);

export type DocumentTypeValue = z.infer<typeof DocumentTypeSchema>;

/**
 * Document category values for validation
 */
export const DocumentCategorySchema = z.enum([
  DOCUMENT_CATEGORIES.FINANCIAL,
  DOCUMENT_CATEGORIES.PROPERTY,
  DOCUMENT_CATEGORIES.IDENTITY,
  DOCUMENT_CATEGORIES.LEGAL,
  DOCUMENT_CATEGORIES.LOAN,
  DOCUMENT_CATEGORIES.AUTHORIZATION,
  DOCUMENT_CATEGORIES.OTHER,
]);

export type DocumentCategoryValue = z.infer<typeof DocumentCategorySchema>;

/**
 * Document verification status values for validation
 */
export const DocumentVerificationStatusSchema = z.enum([
  DOCUMENT_VERIFICATION_STATUS.PENDING,
  DOCUMENT_VERIFICATION_STATUS.VERIFIED,
  DOCUMENT_VERIFICATION_STATUS.REJECTED,
  DOCUMENT_VERIFICATION_STATUS.EXPIRED,
  DOCUMENT_VERIFICATION_STATUS.NEEDS_REUPLOAD,
]);

export type DocumentVerificationStatusValue = z.infer<typeof DocumentVerificationStatusSchema>;

/**
 * Loan type values for validation (for checklist association).
 *
 * @deprecated Issue #1388 — Replace with `loanProductId: z.string().uuid()` on
 * attachment metadata once document-pipeline checklists migrate to per-business
 * `LoanProduct` references. Kept during the transition window so existing
 * checklist rows keep validating.
 */
export const LoanTypeSchema = z.enum([
  LOAN_TYPES.BRIDGE,
  LOAN_TYPES.FIX_AND_FLIP,
  LOAN_TYPES.DSCR,
  LOAN_TYPES.RENTAL,
  LOAN_TYPES.GROUND_UP,
  LOAN_TYPES.CONSTRUCTION,
  LOAN_TYPES.COMMERCIAL,
  LOAN_TYPES.MULTIFAMILY,
  LOAN_TYPES.MULTI_FAMILY,
  LOAN_TYPES.LAND,
  LOAN_TYPES.MIXED_USE,
]);

export type LoanTypeValue = z.infer<typeof LoanTypeSchema>;

// ============================================================================
// ENTITY ATTACHMENT RESPONSE SCHEMA
// ============================================================================

/**
 * Entity Attachment DTO Schema (output)
 */
export const EntityAttachmentSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  entityType: EntityAttachmentTypeSchema,
  entityId: z.string().uuid(),
  documentId: z.string().uuid(),
  documentType: DocumentTypeSchema.nullable().optional(),
  category: DocumentCategorySchema.nullable().optional(),
  verificationStatus: DocumentVerificationStatusSchema,
  verifiedBy: z.string().uuid().nullable().optional(),
  verifiedAt: z.date().nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  expiresAt: z.date().nullable().optional(),
  notes: z.string().nullable().optional(),
  displayOrder: z.number().int().default(0),
  displayName: z.string().nullable().optional(),
  attachedBy: z.string().uuid(),
  // Data Room source tracking (Issue #871)
  sourceEntityType: EntityAttachmentTypeSchema.nullable().optional(),
  sourceEntityId: z.string().uuid().nullable().optional(),
  linkedFromDataRoom: z.boolean().default(false),
  createdAt: z.date(),
  updatedAt: z.date(),
  // Joined document data (optional)
  document: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      originalName: z.string(),
      mimeType: z.string(),
      size: z.number().int().positive(),
      status: z.string(),
    })
    .optional(),
});

export type EntityAttachment = z.infer<typeof EntityAttachmentSchema>;

// ============================================================================
// ATTACH DOCUMENT SCHEMAS
// ============================================================================

/**
 * Attach Document Request DTO Schema
 * For tRPC attachments.attach procedure
 */
export const AttachDocumentRequestSchema = z.object({
  entityType: EntityAttachmentTypeSchema,
  entityId: z.string().uuid(),
  documentId: z.string().uuid(),
  documentType: DocumentTypeSchema.optional(),
  category: DocumentCategorySchema.optional(),
  notes: z.string().max(1000).optional(),
  expiresAt: z.date().optional(),
  displayOrder: z.number().int().min(0).optional(),
});

export type AttachDocumentRequest = z.infer<typeof AttachDocumentRequestSchema>;

/**
 * Detach Document Request DTO Schema
 * For tRPC attachments.detach procedure
 */
export const DetachDocumentRequestSchema = z.object({
  attachmentId: z.string().uuid(),
});

export type DetachDocumentRequest = z.infer<typeof DetachDocumentRequestSchema>;

/**
 * Update Attachment Request DTO Schema
 * For tRPC attachments.update procedure
 */
export const UpdateAttachmentRequestSchema = z.object({
  id: z.string().uuid(),
  documentType: DocumentTypeSchema.optional(),
  category: DocumentCategorySchema.optional(),
  notes: z.string().max(1000).optional(),
  expiresAt: z.date().nullable().optional(),
  displayOrder: z.number().int().min(0).optional(),
  displayName: z.string().max(255).nullable().optional(),
});

export type UpdateAttachmentRequest = z.infer<typeof UpdateAttachmentRequestSchema>;

/**
 * List Attachments Query DTO Schema
 * For tRPC attachments.list procedure
 */
export const ListAttachmentsQuerySchema = ListQuerySchema.extend({
  entityType: EntityAttachmentTypeSchema,
  entityId: z.string().uuid(),
  documentType: DocumentTypeSchema.optional(),
  category: DocumentCategorySchema.optional(),
  verificationStatus: DocumentVerificationStatusSchema.optional(),
});

export type ListAttachmentsQuery = z.infer<typeof ListAttachmentsQuerySchema>;

/**
 * List Attachments Response DTO Schema
 */
export const ListAttachmentsResponseSchema = z.object({
  items: z.array(EntityAttachmentSchema),
  total: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});

export type ListAttachmentsResponse = z.infer<typeof ListAttachmentsResponseSchema>;

// ============================================================================
// VERIFICATION WORKFLOW SCHEMAS
// ============================================================================

/**
 * Verify Attachment Request DTO Schema
 * For tRPC attachments.verify procedure
 */
export const VerifyAttachmentRequestSchema = z.object({
  id: z.string().uuid(),
});

export type VerifyAttachmentRequest = z.infer<typeof VerifyAttachmentRequestSchema>;

/**
 * Reject Attachment Request DTO Schema
 * For tRPC attachments.reject procedure
 */
export const RejectAttachmentRequestSchema = z.object({
  id: z.string().uuid(),
  rejectionReason: z.string().min(1, 'Rejection reason is required').max(1000),
});

export type RejectAttachmentRequest = z.infer<typeof RejectAttachmentRequestSchema>;

/**
 * Request Reupload Request DTO Schema
 * For tRPC attachments.requestReupload procedure
 */
export const RequestReuploadRequestSchema = z.object({
  id: z.string().uuid(),
  reason: z.string().min(1, 'Reupload reason is required').max(1000),
});

export type RequestReuploadRequest = z.infer<typeof RequestReuploadRequestSchema>;

// ============================================================================
// DOCUMENT CHECKLIST SCHEMAS
// ============================================================================

/**
 * Checklist Item DTO Schema
 */
export const DocumentChecklistItemSchema = z.object({
  id: z.string().uuid(),
  checklistId: z.string().uuid(),
  documentType: DocumentTypeSchema,
  displayName: z.string(),
  description: z.string().nullable().optional(),
  isRequired: z.boolean().default(true),
  displayOrder: z.number().int().default(0),
  // Fulfillment status (computed from attachments)
  isFulfilled: z.boolean().default(false),
  fulfilledBy: z.string().uuid().nullable().optional(), // attachmentId
  fulfilledAt: z.date().nullable().optional(),
});

export type DocumentChecklistItem = z.infer<typeof DocumentChecklistItemSchema>;

/**
 * Document Checklist DTO Schema
 */
export const DocumentChecklistSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  loanType: LoanTypeSchema.nullable().optional(),
  isTemplate: z.boolean().default(false),
  entityType: EntityAttachmentTypeSchema.nullable().optional(),
  entityId: z.string().uuid().nullable().optional(),
  createdBy: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
  // Items
  items: z.array(DocumentChecklistItemSchema).optional(),
  // Progress (computed)
  totalItems: z.number().int().nonnegative().optional(),
  completedItems: z.number().int().nonnegative().optional(),
  requiredItems: z.number().int().nonnegative().optional(),
  completedRequiredItems: z.number().int().nonnegative().optional(),
});

export type DocumentChecklist = z.infer<typeof DocumentChecklistSchema>;

/**
 * Create Document Checklist Request DTO Schema
 */
export const CreateDocumentChecklistRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  loanType: LoanTypeSchema.optional(),
  isTemplate: z.boolean().default(false),
  entityType: EntityAttachmentTypeSchema.optional(),
  entityId: z.string().uuid().optional(),
  items: z
    .array(
      z.object({
        documentType: DocumentTypeSchema,
        displayName: z.string().min(1).max(255),
        description: z.string().max(500).optional(),
        isRequired: z.boolean().default(true),
        displayOrder: z.number().int().min(0).optional(),
      }),
    )
    .optional(),
});

export type CreateDocumentChecklistRequest = z.infer<typeof CreateDocumentChecklistRequestSchema>;

/**
 * Update Document Checklist Request DTO Schema
 */
export const UpdateDocumentChecklistRequestSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(1000).nullable().optional(),
  loanType: LoanTypeSchema.nullable().optional(),
});

export type UpdateDocumentChecklistRequest = z.infer<typeof UpdateDocumentChecklistRequestSchema>;

/**
 * Add Checklist Item Request DTO Schema
 */
export const AddChecklistItemRequestSchema = z.object({
  checklistId: z.string().uuid(),
  documentType: DocumentTypeSchema,
  displayName: z.string().min(1).max(255),
  description: z.string().max(500).optional(),
  isRequired: z.boolean().default(true),
  displayOrder: z.number().int().min(0).optional(),
});

export type AddChecklistItemRequest = z.infer<typeof AddChecklistItemRequestSchema>;

/**
 * Update Checklist Item Request DTO Schema
 */
export const UpdateChecklistItemRequestSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().min(1).max(255).optional(),
  description: z.string().max(500).nullable().optional(),
  isRequired: z.boolean().optional(),
  displayOrder: z.number().int().min(0).optional(),
});

export type UpdateChecklistItemRequest = z.infer<typeof UpdateChecklistItemRequestSchema>;

/**
 * Remove Checklist Item Request DTO Schema
 */
export const RemoveChecklistItemRequestSchema = z.object({
  id: z.string().uuid(),
});

export type RemoveChecklistItemRequest = z.infer<typeof RemoveChecklistItemRequestSchema>;

/**
 * List Checklists Query DTO Schema
 */
export const ListChecklistsQuerySchema = ListQuerySchema.extend({
  loanType: LoanTypeSchema.optional(),
  isTemplate: z.boolean().optional(),
  entityType: EntityAttachmentTypeSchema.optional(),
  entityId: z.string().uuid().optional(),
});

export type ListChecklistsQuery = z.infer<typeof ListChecklistsQuerySchema>;

/**
 * List Checklists Response DTO Schema
 */
export const ListChecklistsResponseSchema = z.object({
  items: z.array(DocumentChecklistSchema),
  total: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});

export type ListChecklistsResponse = z.infer<typeof ListChecklistsResponseSchema>;

/**
 * Required document status in the data room (merged checklist view)
 * Issue #1145: Document checklist merged into data room
 */
export const RequiredDocumentStatusSchema = z.enum([
  'missing', // No document uploaded for this requirement
  'pending', // Document uploaded, awaiting verification
  'verified', // Document verified/approved
  'rejected', // Document rejected
  'needs_reupload', // Re-upload requested
  'expired', // Document verified but stale (maxAgeMonths exceeded)
]);

export type RequiredDocumentStatus = z.infer<typeof RequiredDocumentStatusSchema>;

/**
 * Checklist Progress DTO Schema
 * For UI progress display
 */
export const ChecklistProgressSchema = z.object({
  checklistId: z.string().uuid(),
  checklistName: z.string(),
  totalItems: z.number().int().nonnegative(),
  completedItems: z.number().int().nonnegative(),
  requiredItems: z.number().int().nonnegative(),
  completedRequiredItems: z.number().int().nonnegative(),
  percentComplete: z.number().min(0).max(100),
  percentRequiredComplete: z.number().min(0).max(100),
  isComplete: z.boolean(), // All required items fulfilled
  items: z.array(
    z.object({
      id: z.string().uuid(),
      documentType: DocumentTypeSchema,
      displayName: z.string(),
      isRequired: z.boolean(),
      isFulfilled: z.boolean(),
      /** Granular status: verified, pending, rejected, needs_reupload, missing */
      status: RequiredDocumentStatusSchema.optional(),
      attachment: EntityAttachmentSchema.nullable().optional(),
    }),
  ),
});

export type ChecklistProgress = z.infer<typeof ChecklistProgressSchema>;

// ============================================================================
// DOCUMENT SCOPE SCHEMA
// ============================================================================

export const DocumentScopeSchema = z.nativeEnum(DocumentScope);

// ============================================================================
// DATA ROOM DTOs
// ============================================================================

export const DataRoomViewRequestSchema = z.object({
  caseId: z.string().uuid(),
});

export const DataRoomSourceSchema = z.object({
  sourceEntityType: EntityAttachmentTypeSchema,
  sourceEntityId: z.string().uuid(),
  sourceName: z.string(),
  sourceLabel: z.string(),
  documents: z.array(EntityAttachmentSchema),
});

/**
 * Required document item for the data room merged checklist view.
 * Represents either a placeholder (unfulfilled requirement) or a fulfilled requirement
 * merged with its actual document.
 *
 * Issue #1145: Document checklist merged into data room
 */
export const RequiredDocumentItemSchema = z.object({
  /** The checklist item ID */
  checklistItemId: z.string().uuid(),
  /** Document type from DOCUMENT_TYPES taxonomy */
  documentType: z.string(),
  /** Document types that can fulfill this requirement */
  acceptsTypes: z.array(z.string()).default([]),
  /** Human-readable requirement name (e.g., "Personal Tax Returns (Last 2 Years)") */
  displayName: z.string(),
  /** Requirement description */
  description: z.string().nullable(),
  /** Document category (personal, property, financial, legal) */
  category: z.string().nullable(),
  /** Whether this document is required (vs optional) */
  isRequired: z.boolean(),
  /** Current fulfillment status */
  status: RequiredDocumentStatusSchema,
  /** The attachment that fulfills this requirement (null when status is 'missing') */
  attachment: EntityAttachmentSchema.nullable(),
  /** Rejection or re-upload reason (when status is rejected/needs_reupload) */
  rejectionReason: z.string().nullable(),
  /** When the requirement was fulfilled */
  fulfilledAt: z.coerce.date().nullable(),
});

export type RequiredDocumentItem = z.infer<typeof RequiredDocumentItemSchema>;

export const DataRoomViewResponseSchema = z.object({
  caseId: z.string().uuid(),
  loanDocuments: z.array(EntityAttachmentSchema),
  sources: z.array(DataRoomSourceSchema),
  checklistProgress: ChecklistProgressSchema.nullable(),
  /** Required documents from the case's checklist, merged with their attachments.
   *  Unfulfilled items appear as placeholders; fulfilled items include attachment details.
   *  Issue #1145: Document checklist merged into data room */
  requiredDocuments: z.array(RequiredDocumentItemSchema),
});

export const DataRoomBrowseSourcesRequestSchema = z.object({
  caseId: z.string().uuid(),
});

export const DataRoomBrowseSourceDocumentSchema = z.object({
  documentId: z.string().uuid(),
  name: z.string(),
  documentType: z.string().nullable(),
  category: z.string().nullable(),
  defaultScope: DocumentScopeSchema.nullable(),
  alreadyLinked: z.boolean(),
  verificationStatus: DocumentVerificationStatusSchema.nullable(),
  attachedAt: z.coerce.date().nullable(),
});

export const DataRoomBrowseSourceSchema = z.object({
  sourceEntityType: EntityAttachmentTypeSchema,
  sourceEntityId: z.string().uuid(),
  sourceName: z.string(),
  sourceLabel: z.string(),
  documents: z.array(DataRoomBrowseSourceDocumentSchema),
});

export const DataRoomBrowseSourcesResponseSchema = z.object({
  sources: z.array(DataRoomBrowseSourceSchema),
});

export const DataRoomLinkDocumentRequestSchema = z.object({
  caseId: z.string().uuid(),
  documentId: z.string().uuid(),
  sourceEntityType: EntityAttachmentTypeSchema,
  sourceEntityId: z.string().uuid(),
  scopeOverride: DocumentScopeSchema.optional(),
});

export const DataRoomUnlinkDocumentRequestSchema = z.object({
  caseId: z.string().uuid(),
  attachmentId: z.string().uuid(),
});

export const DataRoomBulkLinkRequestSchema = z.object({
  caseId: z.string().uuid(),
  documents: z
    .array(
      z.object({
        documentId: z.string().uuid(),
        sourceEntityType: EntityAttachmentTypeSchema,
        sourceEntityId: z.string().uuid(),
        scopeOverride: DocumentScopeSchema.optional(),
      }),
    )
    .min(1)
    .max(50),
});

export const DataRoomMyReusableDocsRequestSchema = z.object({
  caseId: z.string().uuid(),
});

// ============================================================================
// DOCUMENT MANAGEMENT SCHEMAS
// ============================================================================

/**
 * Rename Document Request DTO Schema
 * For tRPC documents.rename procedure
 */
export const RenameDocumentRequestSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
});

export type RenameDocumentRequest = z.infer<typeof RenameDocumentRequestSchema>;

/**
 * Move Attachment Request DTO Schema
 * For tRPC attachments.move procedure
 */
export const MoveAttachmentRequestSchema = z.object({
  attachmentId: z.string().uuid(),
  newEntityType: EntityAttachmentTypeSchema,
  newEntityId: z.string().uuid(),
});

export type MoveAttachmentRequest = z.infer<typeof MoveAttachmentRequestSchema>;

/**
 * Delete Document Request DTO Schema
 * For tRPC documents.delete procedure
 */
export const DeleteDocumentRequestSchema = z.object({
  id: z.string().uuid(),
  force: z.boolean().optional(),
});

export type DeleteDocumentRequest = z.infer<typeof DeleteDocumentRequestSchema>;

// Type exports
export type DataRoomViewRequest = z.infer<typeof DataRoomViewRequestSchema>;
export type DataRoomViewResponse = z.infer<typeof DataRoomViewResponseSchema>;
export type DataRoomBrowseSourcesRequest = z.infer<typeof DataRoomBrowseSourcesRequestSchema>;
export type DataRoomBrowseSourcesResponse = z.infer<typeof DataRoomBrowseSourcesResponseSchema>;
export type DataRoomLinkDocumentRequest = z.infer<typeof DataRoomLinkDocumentRequestSchema>;
export type DataRoomUnlinkDocumentRequest = z.infer<typeof DataRoomUnlinkDocumentRequestSchema>;
export type DataRoomBulkLinkRequest = z.infer<typeof DataRoomBulkLinkRequestSchema>;
export type DataRoomMyReusableDocsRequest = z.infer<typeof DataRoomMyReusableDocsRequestSchema>;
